/**
 * governance/utils/contentPatterns.ts
 * Content validation patterns: generic text detection, didactic quality checks,
 * module-specific depth markers, and shared regex constants.
 */

import type { Question } from '../../content';

// ─── Shared Constants ─────────────────────────────────────────────────────────

/**
 * Question types that are NOT multiple-choice and should skip distractor analysis.
 * Single source of truth — used by didacticDepth.ts and questionQualityEngine.ts.
 */
export const NON_MC_TYPES: Question['questionType'][] = [
// Practice-only interactive types
'fill_in', 'drag_drop_fill', 'ordering', 'text_highlight', 'number_sequence', 'matching', 'ratio_table', 'table_reading',
// Visual data types
'bar_chart', 'line_chart', 'pie_chart', 'schedule_reading',
// Specialized arithmetic types
'cijferend_rekenen', 'cijferend_delen',
// Canonical custom types
'clock', 'clock_set', 'money', 'multiplication_grid', 'true_false_series', 'estimation_slider',
// Newer interactive types
'categorize', 'error_spotting', 'grid_shade', 'step_solver', 'sentence_parser', 'comparison_judge', 'thermometer_slider', 'sentence_error_judge', 'verb_conjugation_flow'];

// ─── Content Stage Regex Constants ────────────────────────────────────────────

/**
 * Check-strategy pattern — recognizes Dutch pedagogical verification strategies.
 * Used by contentStage to validate that explanations (per-module tier-gated) include
 * a check / controle step. Covers three families of check-vocab:
 *   1. SWW/SPR werkwoordstijden-checks (ik-vorm, hij-vorm, infinitief, stam+t, …)
 *   2. Universele check-werkwoorden (controleer, check, narekenen, klopt, tegenproef, …)
 *   3. REK/GET/MET/GELD math-verification cues (tegenproef, terugrekenen,
 *      'reken na', 'klopt het', '✓' check-marker, 'is het logisch', 'past het',
 *      'is dat genoeg', 'in de buurt', omgekeerde bewerking)
 *
 * GSR-27 (edtech-review 28 juni): de SWW-seeds noteren de werkwoordstijd-check
 * vaak met de standaard-afkortingen VD / VT / TT i.p.v. de voluit geschreven
 * "voltooid deelwoord" / "verleden tijd" / "tegenwoordige tijd". Die afkortingen
 * zijn universele SWW-shorthand en vervullen exact dezelfde check-rol — daarom
 * als woord-gebonden tokens toegevoegd. Pure uitbreiding: geen bestaand
 * alternatief verwijderd.
 */
export const CHECK_STRATEGY_PATTERN = /ik-vorm|hij-vorm|langer maken|worden|ik loop|hij loopt|zet.*om|controleer|controle\b|check\b|infinitief|hele werkwoord|stam\s*\+?\s*t\b|verleden tijd|tegenwoordige tijd|voltooid deelwoord|\bVD\b|\bVT\b|\bTT\b|vul.*in|probeer.*zin|enkelvoud|meervoud|narekenen|reken na|tegenproef|klopt het|klopt dat|is het logisch|in de buurt|past het|is dat genoeg|terugrekenen|terug rekenen|omgekeerde bewerking|✓|kijk of/i;

/**
 * Topo geographic depth pattern — recognizes geographic fact references in explanations.
 * Used by contentStage to exempt Topo explanations from generic didactic depth checks.
 */
export const TOPO_GEOGRAPHIC_DEPTH_PATTERN = /provincie|hoofdstad|rivier|land|continent|oceaan|gebergte|zee|grens|eiland|klimaat|berg|dal|kust|noorden|zuiden|oosten|westen/i;

// ─── Generic Text Patterns ────────────────────────────────────────────────────

/** Patterns that indicate a generic, boilerplate explanation */
export const GENERIC_EXPLANATION_PATTERNS: RegExp[] = [/^het goede antwoord is/i, /^het antwoord is/i, /^dit is (het )?correct/i, /^correct[.!]?\s*$/i, /^goed[.!]?\s*$/i, /^fout[.!]?\s*$/i, /^zie uitleg/i, /^kijk naar/i, /^let op[.!]?\s*$/i, /^antwoord:/i, /^het juiste antwoord/i, /^dit antwoord is/i, /^kijk rustig naar de vraag/i, /^werk stap voor stap\.\s*zo zie je/i, /^zo zie je waarom/i, /^vergelijk met het juiste antwoord/i, /^let op de belangrijkste aanwijzing/i, /^denk nog eens goed na/i, /^lees de vraag nog eens/i, /^pas niet precies bij de vraag/i, /^nog niet goed\./i, /^het antwoord is niet juist/i, /^probeer het opnieuw/i, /^dat is (niet )?juist/i, /^helaas/i, /^jammer/i, /^goed gedaan[.!]?\s*$/i, /^prima[.!]?\s*$/i, /^klopt[.!]?\s*$/i,
// v5.8: Forbidden explanation openers from CONTENT_GOVERNANCE_FRAMEWORK §10.4
/^strategie\s*:/i, /^regel\s*:/i, /^stap\s*1\s*:/i, /^misconceptie\s*:/i, /^tip\s*:/i];

/** Patterns that indicate a generic distractor explanation */
export const GENERIC_DISTRACTOR_PATTERNS: RegExp[] = [/^dit is (niet )?correct/i, /^fout antwoord/i, /^verkeerd/i, /^niet goed/i, /^onjuist[.!]?\s*$/i, /^probeer opnieuw/i, /^dat klopt niet/i, /^dit is niet het goede antwoord/i, /^helaas, dit is niet/i, /^dit antwoord is fout/i, /^niet het goede antwoord/i, /^kijk nog eens/i, /^pas niet bij/i, /^verkeerd antwoord/i, /^nee,?\s*(dit is)?\s*(niet)?\s*(correct|goed|juist)/i, /^dit kan niet/i, /^veelgemaakte fout/i, /^helaas[,.]?\s*$/i, /^jammer[,.]?\s*$/i, /^probeer het opnieuw/i, /^kijk nog eens naar de vraag/i,
// v5.8: Additional forbidden distractor openers from calibration work
/^fout\s*:/i, /^correct\s*!/i, /^correct\s*\./i, /^strategie\s*:/i, /^misconceptie\s*:/i, /^regel\s*:/i, /^tip\s*:/i];

/**
 * Forbidden opener patterns — shared between explanation and distractor checks.
 * Used by contentStage.ts for the CONTENT_FORBIDDEN_OPENER issue.
 */
export const FORBIDDEN_OPENER_PATTERNS: ReadonlyArray<{
  pattern: RegExp;
  label: string;
}> = [{
  pattern: /^strategie\s*:/i,
  label: 'strategie:'
}, {
  pattern: /^veelgemaakte fout/i,
  label: 'veelgemaakte fout'
}, {
  pattern: /^misconceptie\s*:/i,
  label: 'misconceptie:'
}, {
  pattern: /^tip\s*:/i,
  label: 'tip:'
}] as const;

// ─── Module-specific domain-vocab patterns ────────────────────────────────────
// NOTE (GSR-20, audit 2026): the GET regex below was previously called
// `GET_MISCONCEPTIONS` which collided with the unrelated data-array of the
// same name in `governance/data/misconceptions/get.ts`. Renamed to
// `GET_DOMAIN_VOCAB` — semantically it's a list of domain vocabulary terms
// used to detect module-specific depth in explanations, NOT a list of
// misconceptions. Pure rename, no functional change.

export const GET_DOMAIN_VOCAB = /plaatswaarde|komma|afrond|decimaal|breuk|noemer|teller|macht|wortel|procent|verhouding/i;
export const SWW_RULES = /stam|kofschip|dt-regel|verdubbel|verenkeld|persoonsvorm|infinitief|voltooid deelwoord/i;
export const SWW_CHECK_STRATEGY = /ik-vorm|hij-vorm|infinitief|stam\b/i;
export const SPR_RULES = /open lettergreep|gesloten lettergreep|trema|koppelteken|tussen-n|samengesteld/i;
export const MET_FORMULA = /formule|oppervlakte|omtrek|inhoud|π|pi|\bL\s*[×x\*]\s*B\b|kwadraat|kubiek|omrekenen|eenheid/i;
export const TGL_CALC = /\d+\s*[×x\*÷\/\+\-]\s*\d+/;
export const TGL_ERRORS = /eenheid|minuten|uren|seconden|euro|cent|wisselgeld|tijdzone|notatie/i;
export const TAA_CITE = /tekst|alinea|zin|regel|grammatica|werkwoord|zelfstandig naamwoord|bijvoeglijk|lidwoord/i;
export const STU_STRATEGY = /strategie|samenvatten|skimmen|scannen|hoofd(gedachte|idee)|steekwoord|mindmap|schema/i;

// ─── Detection Functions ──────────────────────────────────────────────────────

/**
 * Returns true if the text matches any of the given generic patterns.
 * FIXED: For explanation patterns, texts that start with a generic opener but
 * continue with substantive didactic content (>60 chars + didactic markers)
 * are NOT flagged as generic.
 */
export function isGenericText(text: string, patterns: RegExp[]): boolean {
  const trimmed = text.trim();
  if (!patterns.some(p => p.test(trimmed))) return false;
  if (trimmed.length <= 60) return true;
  const isOpenerMatch = patterns.some(p => {
    const source = p.source;
    return source.startsWith('^') && !source.includes('$') && p.test(trimmed);
  });
  if (isOpenerMatch) {
    if (isDidactic(trimmed)) return false;
    const hasSubstance = /regel:|formule:|stap\s*\d|omdat|want|dus\b.*[.!]/i.test(trimmed) || /[<>≤≥≠]=?\s*-?\d/.test(trimmed) || /\d+\s*[×x\*÷\/\+\-]\s*\d+/.test(trimmed) || trimmed.split(/[.!]/).filter(s => s.trim().length > 15).length >= 2;
    if (hasSubstance) return false;
  }
  return true;
}

/**
 * Returns true if the text shows step-by-step or didactic reasoning.
 * Recognizes Dutch pedagogical patterns, mathematical notation, and
 * structured reasoning.
 */
export function isDidactic(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length < 25) return false;
  const hasCalculation = /\d+\s*[×x\*÷\/\+\-]\s*\d+\s*=\s*\d+/.test(trimmed);
  const hasStepStructure = /stap\s*\d|eerst.*dan|vervolgens.*dan|1\.\s|2\.\s|a\)\s|b\)\s/i.test(trimmed);
  const hasCausalReasoning = /omdat.*daarom|want.*dus|als.*dan|doordat|hierdoor|waardoor/i.test(trimmed);
  const hasExplicitRule = /\bregel\s*:|de regel|de formule|de wet|het principe|de methode/i.test(trimmed);
  const hasComparison = /-?\d+\s*[<>≤≥≠]\s*-?\d+/.test(trimmed) || /kleiner dan|groter dan|gelijk aan|meer dan|minder dan/i.test(trimmed);
  const hasComparativeReasoning = /hoe\s+\w+.*hoe\s+\w+/i.test(trimmed);
  const hasConclusionWithMath = /\bdus\b.*-?\d/i.test(trimmed) && trimmed.length >= 40;
  const hasQuestionAnswer = /\b(is|zijn|was|wordt)\b.+\?\s*(ja|nee)/i.test(trimmed);
  const hasArrowTransform = /→/.test(trimmed) && /\d/.test(trimmed);
  const hasIdentifyAndConclude = /dat is (de|het|een)\s+\w+/i.test(trimmed) && /\bdus\b/i.test(trimmed);
  const hasPedagogicalDirective = /\b(onthoud|controleer|check|pas op|let op)\s*:/i.test(trimmed);
  const hasMisconceptionAddress = /je dacht (waarschijnlijk|misschien)/i.test(trimmed);
  const hasDirectedLook = /kijk naar (het|de|een)\s+\w+.*:\s*(de|het|dat|die|\d)/i.test(trimmed);
  if (hasCalculation || hasStepStructure || hasCausalReasoning || hasExplicitRule || hasComparison || hasComparativeReasoning || hasConclusionWithMath || hasQuestionAnswer || hasArrowTransform || hasIdentifyAndConclude || hasPedagogicalDirective || hasMisconceptionAddress || hasDirectedLook) return true;
  let weakSignals = 0;
  if (/omdat|want|dus|daarom/i.test(trimmed)) weakSignals++;
  if (/stap|eerst|dan|vervolgens/i.test(trimmed)) weakSignals++;
  if (trimmed.includes('=') || /[<>≤≥≠]/.test(trimmed)) weakSignals++;
  if (/\d+\s*[×x\*÷\/\+\-]\s*\d+/.test(trimmed)) weakSignals++;
  if (/links.*rechts|rechts.*links|getallenlijn/i.test(trimmed)) weakSignals++;
  if (/→/.test(trimmed)) weakSignals++;
  if (/onthoud|controleer|check/i.test(trimmed)) weakSignals++;
  if (/je dacht/i.test(trimmed)) weakSignals++;
  if (/\d.*\bwordt\b.*\d/i.test(trimmed)) weakSignals++;
  return weakSignals >= 2;
}

/**
 * Returns true if the explanation is substantive (not just didactic markers,
 * but actually explains something meaningful).
 */
export function isSubstantiveExplanation(text: string, module: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length < 40) return false;
  if (isGenericText(trimmed, GENERIC_EXPLANATION_PATTERNS)) return false;
  const hasModuleDepth = hasModuleSpecificDepth(trimmed, module);
  const hasDidacticQuality = isDidactic(trimmed);
  if (hasModuleDepth && hasDidacticQuality) return true;
  if ((hasModuleDepth || hasDidacticQuality) && trimmed.length >= 60) return true;
  return false;
}

/**
 * Returns true if the explanation contains module-specific depth markers
 */
export function hasModuleSpecificDepth(text: string, module: string): boolean {
  switch (module) {
    case 'Getalbegrip':
      return /\d+\s*[×x\*÷\/\+\-]\s*\d+/.test(text) || text.includes('=') || /-?\d+\s*[<>≤≥≠]\s*-?\d+/.test(text) || /\bregel\s*:/i.test(text) || /getallenlijn|links.*rechts|rechts.*links/i.test(text) || /negatief|positief|minteken|plusteken/i.test(text) || /kleiner|groter|gelijk/i.test(text);
    case 'SpellingWW':
      return SWW_RULES.test(text);
    case 'SpellingRegel':
      return SPR_RULES.test(text);
    case 'Metriek':
      return MET_FORMULA.test(text);
    case 'Tijd':
      return TGL_CALC.test(text) || /minuten|uren|seconden|klok|kalender|snelheid/i.test(text);
    case 'Geld':
      return TGL_CALC.test(text) || /euro|cent|wisselgeld|korting|btw|budget/i.test(text);
    case 'Basisrekenen':
      return /\d+\s*[×x\*÷\/\+\-]\s*\d+/.test(text) || /strategie|splitsen|compenseren|rijgen|schatten/i.test(text);
    case 'Taalbegrip':
      return TAA_CITE.test(text);
    case 'Studievaardig':
      return STU_STRATEGY.test(text);
    case 'Topo':
      return /provincie|hoofdstad|rivier|land|continent|oceaan|gebergte|zee|eiland|grens|stad|berg|kust|noorden|zuiden|oosten|westen|stroomt|ligt|grenst|klimaat/i.test(text);
    default:
      return isDidactic(text);
  }
}